import { useEffect, useRef, useState } from "react";
import { Link as RLink, useNavigate, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Phone, Loader2, ChevronLeft } from "lucide-react";
import { sendChat, api, chatbotBootstrap } from "../lib/api";
import { siteConfig } from "../config";

const FALLBACK_GREETING =
  "Hi, I'm Scott's virtual assistant. I can help you find the right Chevy or used vehicle, value a trade, answer payment or EV questions, and connect you with Scott when you're ready. What kind of vehicle are you looking for?";

const FALLBACK_DISCLAIMER =
  "Virtual assistant only. Please confirm availability, pricing, payments, trade values, approvals, and deal terms directly with Scott.";

const HANDOFF_KEYWORDS = [
  "talk to a real person",
  "talk to a human",
  "real person",
  "real human",
  "speak to scott",
  "talk to scott",
  "text scott",
  "talk to a real",
  "human please",
  "live agent",
  "agent please",
  "send to scott",
  "have scott call",
];

function sessionId() {
  try {
    let s = localStorage.getItem("ssc_chat_session");
    if (!s) {
      s = "ssc_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("ssc_chat_session", s);
    }
    return s;
  } catch {
    return "ssc_" + Date.now().toString(36);
  }
}

/** Render assistant text with markdown-style links + line breaks */
function renderAssistant(text) {
  const safeText = typeof text === "string" ? text : String(text || "");
  const out = [];
  const linkRE = /\[([^\]]+)\]\((\/[^\s)]+)\)/g;
  let last = 0;
  let m;
  let key = 0;

  while ((m = linkRE.exec(safeText)) !== null) {
    if (m.index > last) {
      out.push(<span key={key++}>{safeText.slice(last, m.index)}</span>);
    }

    out.push(
      <RLink
        key={key++}
        to={m[2]}
        className="text-[var(--ssc-blue)] font-bold underline"
        data-testid="chat-vehicle-link"
      >
        {m[1]}
      </RLink>
    );

    last = m.index + m[0].length;
  }

  if (last < safeText.length) {
    out.push(<span key={key++}>{safeText.slice(last)}</span>);
  }

  return out;
}

export default function Chatbot() {
  const nav = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: FALLBACK_GREETING },
  ]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("chat"); // "chat" | "capture"
  const [submitted, setSubmitted] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [lastIntent, setLastIntent] = useState(null);
  const [disclaimer, setDisclaimer] = useState(FALLBACK_DISCLAIMER);

  // Lets backend literally close the chat widget.
  const [isClosingChat, setIsClosingChat] = useState(false);
  const [conversationEnded, setConversationEnded] = useState(false);

  const scrollRef = useRef(null);
  const sid = useRef(sessionId());
  const bootstrapped = useRef(false);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, mode]);

  // Bootstrap on first open: fetch per-page opener, quick-reply chips, and disclaimer.
  useEffect(() => {
    if (!open || bootstrapped.current) return;
    bootstrapped.current = true;

    chatbotBootstrap(location.pathname)
      .then((b) => {
        if (b && b.starter) {
          setMessages([{ role: "assistant", content: b.starter }]);
        }

        if (b && Array.isArray(b.quick_replies)) {
          setQuickReplies(b.quick_replies);
        }

        if (b && b.disclaimer) {
          setDisclaimer(b.disclaimer);
        }
      })
      .catch(() => {
        // Keep fallback greeting/disclaimer.
      });
  }, [open, location.pathname]);

  const resetChatAfterForcedClose = () => {
    try {
      localStorage.removeItem("ssc_chat_session");
    } catch {
      // Ignore storage errors.
    }

    sid.current = sessionId();
    bootstrapped.current = false;
    setMessages([{ role: "assistant", content: FALLBACK_GREETING }]);
    setDraft("");
    setBusy(false);
    setMode("chat");
    setSubmitted(false);
    setShowQuickReplies(true);
    setLastIntent(null);
    setConversationEnded(false);
    setIsClosingChat(false);
  };

  const closeChatFromBackend = () => {
    setIsClosingChat(true);
    setConversationEnded(true);

    setTimeout(() => {
      setOpen(false);
      resetChatAfterForcedClose();
    }, 1400);
  };

  const triggerCapture = () => {
    if (isClosingChat || conversationEnded) return;

    setMode("capture");
    setMessages((arr) => [
      ...arr,
      {
        role: "assistant",
        content:
          "Got it. I’ll connect you with Scott. Drop your details below and he’ll follow up as soon as possible.",
      },
    ]);
  };

  const sendMessage = async (textRaw) => {
    const text = (textRaw || "").trim();

    if (!text || busy || isClosingChat || conversationEnded) return;

    const lower = text.toLowerCase();
    const next = [...messages, { role: "user", content: text }];

    setMessages(next);
    setShowQuickReplies(false);

    if (HANDOFF_KEYWORDS.some((k) => lower.includes(k))) {
      setMode("capture");
      setMessages([
        ...next,
        {
          role: "assistant",
          content:
            "Got it. Let me grab a couple things so Scott can follow up with the right answer.",
        },
      ]);
      // Notify Scott proactively — capture form still collects PII but the
      // event ensures Scott is paged even if the user bails before submitting.
      try {
        api.post("/talk-to-scott", {
          source: "chat_keyword",
          message: `Customer asked to talk to Scott: "${text}"`,
          page_url: typeof window !== "undefined" ? window.location.href : "",
          chat_transcript: messages.slice(-10),
        }).catch(() => { /* ignore */ });
      } catch { /* ignore */ }
      return;
    }

    setBusy(true);

    try {
      const r = await sendChat({
        session_id: sid.current,
        message: text,
        history: messages,
        page_path: location.pathname,
        referrer: document.referrer || null,
      });

      if (r && r.ok) {
        const botReply =
          typeof r.reply === "string" && r.reply.trim()
            ? r.reply
            : "I’m here, but I didn’t get a clean reply back. What vehicle question can I help with?";

        setMessages([
          ...next,
          { role: "assistant", content: botReply, vehicles: Array.isArray(r.vehicles) ? r.vehicles : [] },
        ]);
        setLastIntent(r.intent || null);

        if (r.conversation_ended) {
          setConversationEnded(true);
        }

        // Backend says the chat should literally close.
        if (r.close_chat) {
          closeChatFromBackend();
          return;
        }

        // Auto-prompt handoff when backend classified as high_intent_lead.
        if (r.suggest_handoff && mode === "chat") {
          setTimeout(() => {
            setMessages((arr) => [
              ...arr,
              {
                role: "assistant",
                content:
                  "Want me to send this to Scott so he can reach out with real numbers and availability? Tap **Send to Scott** below.",
              },
            ]);
          }, 800);
        }
      } else {
        const errorReply =
          r && r.error
            ? r.error
            : "Sorry, something went wrong. Please try again, or text Scott directly for help.";

        setMessages([
          ...next,
          {
            role: "assistant",
            content: errorReply,
            showTextScott: true,
          },
        ]);
      }
    } catch {
      setMessages([
        ...next,
        {
          role: "assistant",
          content:
            "Sorry, something went wrong. Please try again, or text Scott directly for help.",
          showTextScott: true,
        },
      ]);
      // Fire-and-forget Talk to Scott alert so Scott still gets notified
      try {
        await api.post("/talk-to-scott", {
          source: "chat_error",
          page_url: typeof window !== "undefined" ? window.location.href : "",
          message: `Customer chat failed on message: "${text}"`,
          chat_transcript: messages.slice(-10),
        });
      } catch { /* ignore */ }
    } finally {
      setBusy(false);
    }
  };

  const send = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (isClosingChat || conversationEnded) return;

    const text = draft.trim();
    if (!text) return;

    setDraft("");
    await sendMessage(text);
  };

  const onQuickReply = (qr) => {
    if (isClosingChat || conversationEnded) return;
    sendMessage(qr.starter || qr.label);
  };

  const closeManually = () => {
    setOpen(false);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="ssc-chat-launcher"
          data-testid="chat-launcher"
          aria-label="Open chat with Scott's virtual assistant"
        >
          <span className="ssc-chat-launcher-avatar" aria-hidden="true">SS</span>
          <span className="hidden sm:flex flex-col items-start leading-tight">
            <span className="font-extrabold text-[13px]">Chat with Scott</span>
            <span className="flex items-center gap-1 text-[11px] font-medium text-white/85">
              <span className="ssc-chat-launcher-dot" /> I&rsquo;m online — let&rsquo;s talk!
            </span>
          </span>
          <span className="sm:hidden">
            <MessageCircle size={22} />
          </span>
        </button>
      )}

      {open && (
        <div className={`ssc-chat-panel${mode === "capture" ? " capturing" : ""}`} data-testid="chat-panel">
          <div className="ssc-chat-header">
            <div className="flex items-center gap-3">
              <div className="ssc-chat-avatar">SS</div>
              <div>
                <div className="font-extrabold leading-tight text-white">
                  Scott Sells Chevys
                </div>
                <div className="text-[11px] text-[var(--ssc-silver)]">
                  {isClosingChat
                    ? "Closing chat"
                    : mode === "capture"
                    ? "Talk to Scott"
                    : "Virtual assistant · replies instantly"}
                </div>
              </div>
            </div>

            <button
              onClick={closeManually}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Close chat"
              data-testid="chat-close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Legal disclaimer: now uses backend/admin setting when available. */}
          {mode !== "capture" && (
            <div
              className="ssc-chat-disclaimer"
              role="note"
              aria-label="Chatbot disclaimer"
              data-testid="chat-disclaimer"
            >
              <span className="ssc-chat-disclaimer-star" aria-hidden="true">
                *
              </span>
              {disclaimer}
            </div>
          )}

          <div className="ssc-chat-body" ref={scrollRef} data-testid="chat-body">
            {messages.map((m, i) => (
              <div key={i}>
                <div
                  className={`ssc-chat-msg ${
                    m.role === "user" ? "ssc-chat-msg-user" : "ssc-chat-msg-bot"
                  }`}
                >
                  {m.role === "assistant" ? renderAssistant(m.content) : m.content}
                </div>
                {m.role === "assistant" && Array.isArray(m.vehicles) && m.vehicles.length > 0 && (
                  <ChatVehicleCards vehicles={m.vehicles} />
                )}
                {m.role === "assistant" && m.showTextScott && (
                  <div className="mt-1.5 mb-2 ml-2">
                    <a
                      href={siteConfig.scott.phoneSms}
                      onClick={() => {
                        // Notify Scott so the lead doesn't get lost
                        api.post("/talk-to-scott", {
                          source: "chat_error_cta",
                          page_url: typeof window !== "undefined" ? window.location.href : "",
                          message: "Customer tapped Text Scott from a chat error.",
                          chat_transcript: messages.slice(-10),
                        }).catch(() => { /* fire and forget */ });
                      }}
                      className="inline-flex items-center gap-1.5 bg-[var(--ssc-blue)] text-white text-xs font-extrabold rounded-full px-3 py-1.5 hover:brightness-110 transition"
                      data-testid="chat-error-text-scott"
                    >
                      <Phone size={12} /> Text Scott directly
                    </a>
                  </div>
                )}
              </div>
            ))}

            {busy && (
              <div className="ssc-chat-msg ssc-chat-msg-bot inline-flex items-center gap-2 text-[var(--ssc-ink-2)]">
                <Loader2 size={14} className="animate-spin" /> typing…
              </div>
            )}

            {isClosingChat && (
              <div className="ssc-chat-msg ssc-chat-msg-bot inline-flex items-center gap-2 text-[var(--ssc-ink-2)]">
                <Loader2 size={14} className="animate-spin" /> closing chat…
              </div>
            )}

            {/* Quick-reply chips: hidden after first user turn so they do not crowd. */}
            {showQuickReplies &&
              quickReplies.length > 0 &&
              mode === "chat" &&
              !isClosingChat &&
              !conversationEnded && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="chat-quick-replies">
                  {quickReplies.map((qr) => (
                    <button
                      key={qr.key}
                      type="button"
                      onClick={() => onQuickReply(qr)}
                      disabled={busy || isClosingChat || conversationEnded}
                      className="text-xs bg-white hover:bg-blue-50 border border-blue-200 text-[var(--ssc-blue)] font-semibold rounded-full px-3 py-1.5 transition-colors disabled:opacity-50"
                      data-testid={`chat-qr-${qr.key}`}
                    >
                      {qr.label}
                    </button>
                  ))}
                </div>
              )}

            {/* Hot-lead nudge badge: only on intent_class=high_intent_lead. */}
            {lastIntent &&
              lastIntent.intent_class === "high_intent_lead" &&
              mode === "chat" &&
              !isClosingChat &&
              !conversationEnded && (
                <button
                  type="button"
                  onClick={triggerCapture}
                  className="mt-2 w-full bg-gradient-to-r from-amber-500 to-red-500 text-white text-xs font-bold rounded px-3 py-2 hover:brightness-110 transition"
                  data-testid="chat-hot-handoff"
                >
                  🔥 Send these details to Scott now →
                </button>
              )}
          </div>

          {mode === "capture" && !submitted && (
            <ContactCaptureForm
              sessionId={sid.current}
              messages={messages}
              onBack={() => setMode("chat")}
              onSubmitted={() => {
                setSubmitted(true);
                setMessages((arr) => [
                  ...arr,
                  {
                    role: "assistant",
                    content:
                      "Thanks, I sent this straight to Scott. He’ll follow up with you as soon as possible.",
                  },
                ]);
                setMode("chat");
              }}
            />
          )}

          {mode === "chat" && (
            <div className="ssc-chat-actions" data-testid="chat-actions">
              <button
                onClick={triggerCapture}
                disabled={isClosingChat || conversationEnded}
                data-testid="chat-action-text"
              >
                Text Scott
              </button>

              <button
                onClick={() => {
                  setOpen(false);
                  nav("/value-my-trade");
                }}
                disabled={isClosingChat || conversationEnded}
                data-testid="chat-action-trade"
              >
                Get My Trade Value
              </button>

              <button
                onClick={() => {
                  setOpen(false);
                  nav("/scotts-deal-desk");
                }}
                disabled={isClosingChat || conversationEnded}
                data-testid="chat-action-build"
              >
                Build My Deal
              </button>

              <button
                onClick={() => {
                  setOpen(false);
                  nav("/schedule-test-drive");
                }}
                disabled={isClosingChat || conversationEnded}
                data-testid="chat-action-schedule"
              >
                Schedule Visit
              </button>
            </div>
          )}

          <div className="ssc-chat-quick">
            <a
              href={siteConfig.scott.phoneSms}
              className="ssc-chat-chip"
              data-testid="chat-text-scott"
            >
              <Phone size={13} /> Text Scott ({siteConfig.scott.phone})
            </a>
          </div>

          {mode === "chat" && (
            <form onSubmit={send} className="ssc-chat-input" data-testid="chat-input-form">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={
                  isClosingChat
                    ? "Closing chat..."
                    : conversationEnded
                    ? "Chat ended"
                    : "Ask anything, or say 'talk to a real person'"
                }
                data-testid="chat-input"
                disabled={busy || isClosingChat || conversationEnded}
              />

              <button
                type="submit"
                disabled={busy || isClosingChat || conversationEnded || !draft.trim()}
                className="ssc-chat-send"
                data-testid="chat-send"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}

function ContactCaptureForm({ sessionId, messages, onBack, onSubmitted }) {
  // Two-step: ask for email first, then offer to capture phone with a graceful retry.
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle_of_interest: "",
    trade_vehicle: "",
    message: "",
    preferred_contact: "Text",
    consent: true,
  });

  // Track whether we've already nudged for phone once.
  const [phoneStage, setPhoneStage] = useState("ask"); // "ask" | "explain" | "skip"
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => {
    setForm((s) => ({
      ...s,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));
  };

  const handleSkipPhone = () => {
    if (phoneStage === "ask") {
      setPhoneStage("explain");
    } else {
      setPhoneStage("skip");
    }
  };

  // Pull a short summary of last user messages as a fallback message body.
  const inferredMessage = (() => {
    const userTurns = (messages || [])
      .filter((m) => m.role === "user")
      .map((m) => m.content);

    return userTurns.slice(-3).join(" · ");
  })();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!form.name.trim() || !form.email.trim()) {
      setErr("Name and email are required.");
      return;
    }

    if (!form.consent) {
      setErr("Please confirm consent to be contacted.");
      return;
    }

    setBusy(true);

    try {
      await api.post("/chat/lead", {
        ...form,
        message: form.message || inferredMessage,
        session_id: sessionId,
      });

      onSubmitted();
    } catch (ex) {
      const detail =
        ex &&
        ex.response &&
        ex.response.data &&
        ex.response.data.detail;

      setErr(
        detail ||
          `Couldn't send right now. Try again or text Scott at ${siteConfig.scott.phone}.`
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="ssc-chat-form" data-testid="chat-capture-form">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-[var(--ssc-ink-2)] hover:text-[var(--ssc-blue)] inline-flex items-center gap-1 self-start"
        data-testid="chat-capture-back"
      >
        <ChevronLeft size={12} /> Back to chat
      </button>

      <div className="row">
        <div>
          <label>Name *</label>
          <input
            value={form.name}
            onChange={set("name")}
            required
            data-testid="capture-name"
            autoComplete="name"
          />
        </div>

        <div>
          <label>Email *</label>
          <input
            value={form.email}
            onChange={set("email")}
            required
            type="email"
            data-testid="capture-email"
            autoComplete="email"
          />
        </div>
      </div>

      {/* Smart phone capture: asks, explains why, then skips quietly. */}
      {phoneStage === "ask" && (
        <div>
          <label>Phone (so Scott can text you back)</label>
          <input
            value={form.phone}
            onChange={set("phone")}
            type="tel"
            data-testid="capture-phone"
            autoComplete="tel"
            placeholder="Optional, but helps a lot"
          />
          <button
            type="button"
            onClick={handleSkipPhone}
            className="text-[10px] text-[var(--ssc-ink-2)] underline mt-1"
            data-testid="capture-phone-skip"
          >
            I'd rather not share
          </button>
        </div>
      )}

      {phoneStage === "explain" && (
        <div
          className="text-xs bg-blue-50 border border-blue-200 rounded-lg p-3"
          data-testid="capture-phone-reason"
        >
          <div className="font-bold text-[var(--ssc-navy)] mb-1">
            Totally up to you.
          </div>
          <div className="text-[var(--ssc-ink)]">
            Scott uses text so he can send back a fast answer when he is not at his desk.
            Without it he can only email, which is usually slower. Email only is still fine.
          </div>

          <div className="mt-2 flex gap-2 items-center flex-wrap">
            <input
              value={form.phone}
              onChange={set("phone")}
              type="tel"
              data-testid="capture-phone-retry"
              placeholder="Phone (optional)"
              className="flex-1 min-w-[140px]"
            />
            <button
              type="button"
              onClick={() => setPhoneStage("skip")}
              className="text-[11px] text-[var(--ssc-ink-2)] underline"
              data-testid="capture-phone-final-skip"
            >
              Email only is fine
            </button>
          </div>
        </div>
      )}

      {phoneStage === "skip" && (
        <div className="text-[11px] text-[var(--ssc-ink-2)]" data-testid="capture-phone-skipped">
          Got it. Email only. Scott will reach out at <strong>{form.email || "your email"}</strong>.
        </div>
      )}

      <div className="row">
        <div>
          <label>Vehicle of interest</label>
          <input
            value={form.vehicle_of_interest}
            onChange={set("vehicle_of_interest")}
            placeholder="e.g. 2024 Silverado"
            data-testid="capture-veh"
          />
        </div>

        <div>
          <label>Trade vehicle</label>
          <input
            value={form.trade_vehicle}
            onChange={set("trade_vehicle")}
            placeholder="optional"
            data-testid="capture-trade"
          />
        </div>
      </div>

      <div>
        <label>Message / Question</label>
        <textarea
          rows={2}
          value={form.message}
          onChange={set("message")}
          placeholder={inferredMessage ? "leave blank to use the chat above" : ""}
          data-testid="capture-msg"
        />
      </div>

      <div>
        <label>Preferred contact</label>
        <select
          value={form.preferred_contact}
          onChange={set("preferred_contact")}
          data-testid="capture-pref"
        >
          <option>Text</option>
          <option>Call</option>
          <option>Email</option>
          <option>Either</option>
        </select>
      </div>

      <label className="flex items-center gap-2 text-xs text-[var(--ssc-ink)] font-medium">
        <input
          type="checkbox"
          checked={form.consent}
          onChange={set("consent")}
          data-testid="capture-consent"
        />
        I consent to be contacted by Scott Sells Chevys.
      </label>

      {err && <div className="text-xs text-red-600" data-testid="capture-error">{err}</div>}

      <button type="submit" disabled={busy} data-testid="capture-submit">
        {busy ? "Sending…" : "Send to Scott"}
      </button>
    </form>
  );
}

/**
 * Renders 1–4 vehicle cards underneath an assistant message when the bot
 * referenced specific inventory items. The backend (`attach_vehicle_links`)
 * only returns vehicles that actually exist in inventory — no invented URLs.
 */
function ChatVehicleCards({ vehicles }) {
  if (!Array.isArray(vehicles) || vehicles.length === 0) return null;
  return (
    <div className="mt-2 space-y-2" data-testid="chat-vehicle-cards">
      {vehicles.slice(0, 4).map((v) => (
        <RLink
          key={v.vin || v.stock_number}
          to={v.url}
          className="block rounded-xl border border-[var(--ssc-line)] bg-white p-2 hover:border-[var(--ssc-blue)] transition"
          data-testid={`chat-vehicle-card-${v.stock_number || v.vin}`}
        >
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-xs font-extrabold text-[var(--ssc-navy)] truncate pr-2">
              {v.title}
            </div>
            <span
              className={`text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0 ${
                v.status === "new"
                  ? "bg-blue-50 text-blue-700"
                  : "bg-amber-50 text-amber-700"
              }`}
            >
              {v.status}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2 mt-1">
            <span className="text-[10px] text-[var(--ssc-ink-2)]">
              Stock #{v.stock_number}
            </span>
            {v.price && (
              <span className="text-[11px] font-extrabold text-[var(--ssc-navy)] tabular-nums">
                ${Number(v.price).toLocaleString()}
              </span>
            )}
          </div>
          <div className="mt-1 text-[10px] font-extrabold text-[var(--ssc-blue)] uppercase tracking-wider">
            {v.cta_label || "View Vehicle"} →
          </div>
        </RLink>
      ))}
    </div>
  );
}
